package com.htmlagent.site.components.widgets

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.htmlagent.site.theme.RevealStyle
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.attrsModifier
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.silk.style.toModifier
import org.w3c.dom.Element

/**
 * Makes a block rise into place the first time it is scrolled into view.
 *
 * It is a modifier and not a wrapper composable on purpose: half the blocks that want this are
 * cells of a grid, and an extra `div` around one of them would take its place in the layout.
 *
 * [delayMs] staggers a row so the cards arrive one after another instead of all at once. It is
 * applied as a delay on the transition and not as a queue in Kotlin, so reduced motion — which
 * the global rule collapses to nothing — removes the wait along with the movement.
 *
 * The element is unobserved as soon as it has appeared: this is an entrance, not a state, and a
 * block that faded out again on the way back up would be a fairground ride.
 *
 * Whether it has appeared is state of the composition and not a mark left on the node, which is
 * not a detail. Written straight to the DOM it lasted until the next recomposition of whatever
 * owns the element — the composition owns that element's attributes and reconciles them, so a
 * block that had already appeared went back to hidden, the observer had long since let go of it,
 * and the band stayed invisible. It cost a section of the landing that emptied itself on the
 * first click inside it.
 */
@Composable
fun Modifier.reveal(delayMs: Int = 0): Modifier {
    var shown by remember { mutableStateOf(false) }

    return then(RevealStyle.toModifier())
        .styleModifier { property("transition-delay", "${delayMs}ms") }
        .attrsModifier {
            attr("data-shown", shown.toString())
            ref { element ->
                val observer = watchForView(element) { shown = true }
                onDispose { observer.disconnect() }
            }
        }
}

/**
 * Watches one element and marks it shown the first time any part of it is on screen.
 *
 * The bottom margin is negative so a block has to be properly inside the viewport, not merely
 * touching its edge, before it animates: at the edge the movement happens where nobody is
 * looking and the block just appears already in place.
 */
private fun watchForView(element: Element, onShown: () -> Unit): ViewObserver {
    val observer = ViewObserver(
        callback = { entries, self ->
            if (entries.any { it.isIntersecting }) {
                onShown()
                self.unobserve(element)
            }
        },
        options = js("({ threshold: 0.12, rootMargin: '0px 0px -8% 0px' })"),
    )
    observer.observe(element)
    return observer
}

/**
 * The browser's `IntersectionObserver`, which the Kotlin DOM library does not declare.
 *
 * Only the three members used are named. The alternative — a scroll listener measuring every
 * block on every frame — is the thing this API exists to replace.
 */
@JsName("IntersectionObserver")
internal external class ViewObserver(
    callback: (entries: Array<ViewEntry>, observer: ViewObserver) -> Unit,
    options: dynamic = definedExternally,
) {
    fun observe(target: Element)
    fun unobserve(target: Element)
    fun disconnect()
}

/** One entry of an [ViewObserver] callback: all that is read from it is whether it is on screen. */
internal external interface ViewEntry {
    val isIntersecting: Boolean
}
